export interface CalculateBMIParams {
  height: number;
  weight: number;
  heightUnit?: 'cm' | 'm' | 'in' | 'ft';
  weightUnit?: 'kg' | 'lbs' | 'g';
}

export interface BMIResult {
  bmi: number;
  category: 'Underweight' | 'Normal weight' | 'Overweight' | 'Obese Class I' | 'Obese Class II' | 'Obese Class III';
  categoryDescription: string;
  healthRisk: 'Low' | 'Normal' | 'Increased' | 'High' | 'Very High' | 'Extremely High';
}

/**
 * Calculates BMI (Body Mass Index) from height and weight
 * Formula: BMI = weight(kg) / height(m)²
 *
 * @param params - Object containing height, weight, and optional unit specifications
 * @returns BMIResult with BMI value, category, description, and health risk
 *
 * @example
 * // Using metric units (default)
 * calculateBMI({ height: 175, weight: 70 }) // height in cm, weight in kg
 *
 * @example
 * // Using imperial units
 * calculateBMI({ height: 69, weight: 154, heightUnit: 'in', weightUnit: 'lb' })
 *
 * @example
 * // Using meters
 * calculateBMI({ height: 1.75, weight: 70, heightUnit: 'm', weightUnit: 'kg' })
 */
export const calculateBMI = (params: CalculateBMIParams): BMIResult => {
  const {height, weight, heightUnit = 'cm', weightUnit = 'kg'} = params;

  // Validate inputs
  if (height <= 0) {
    throw new Error('Height must be greater than 0');
  }
  if (weight <= 0) {
    throw new Error('Weight must be greater than 0');
  }

  // Convert height to meters
  let heightInMeters: number;
  switch (heightUnit) {
    case 'm':
      heightInMeters = height;
      break;
    case 'cm':
      heightInMeters = height / 100;
      break;
    case 'in':
      heightInMeters = height * 0.0254;
      break;
    case 'ft':
      heightInMeters = height * 0.3048;
      break;
    default:
      throw new Error(`Invalid height unit: ${heightUnit}`);
  }

  // Convert weight to kilograms
  let weightInKg: number;
  switch (weightUnit) {
    case 'kg':
      weightInKg = weight;
      break;
    case 'g':
      weightInKg = weight / 1000;
      break;
    case 'lbs':
      weightInKg = weight * 0.453592;
      break;
    default:
      throw new Error(`Invalid weight unit: ${weightUnit}`);
  }

  // Validate converted values are reasonable
  if (heightInMeters < 0.5 || heightInMeters > 3.0) {
    throw new Error('Height must be between 0.5m and 3.0m');
  }
  if (weightInKg < 10 || weightInKg > 500) {
    throw new Error('Weight must be between 10kg and 500kg');
  }

  // Calculate BMI
  const bmi = weightInKg / (heightInMeters * heightInMeters);
  const roundedBmi = Math.round(bmi * 10) / 10;

  // Determine category and health risk
  const {category, categoryDescription, healthRisk} = getCategory(roundedBmi);

  return {
    bmi: roundedBmi,
    category,
    categoryDescription,
    healthRisk,
  };
};

/**
 * Determines the BMI category and health risk based on WHO classifications
 */
function getCategory(bmi: number): {
  category: BMIResult['category'];
  categoryDescription: string;
  healthRisk: BMIResult['healthRisk'];
} {
  if (bmi < 16) {
    return {
      category: 'Underweight',
      categoryDescription: 'Severe thinness (BMI < 16)',
      healthRisk: 'High',
    };
  } else if (bmi < 17) {
    return {
      category: 'Underweight',
      categoryDescription: 'Moderate thinness (BMI 16-17)',
      healthRisk: 'Increased',
    };
  } else if (bmi < 18.5) {
    return {
      category: 'Underweight',
      categoryDescription: 'Mild thinness (BMI 17-18.5)',
      healthRisk: 'Low',
    };
  } else if (bmi < 25) {
    return {
      category: 'Normal weight',
      categoryDescription: 'Normal range (BMI 18.5-25)',
      healthRisk: 'Normal',
    };
  } else if (bmi < 30) {
    return {
      category: 'Overweight',
      categoryDescription: 'Pre-obese (BMI 25-30)',
      healthRisk: 'Increased',
    };
  } else if (bmi < 35) {
    return {
      category: 'Obese Class I',
      categoryDescription: 'Obese Class I (BMI 30-35)',
      healthRisk: 'High',
    };
  } else if (bmi < 40) {
    return {
      category: 'Obese Class II',
      categoryDescription: 'Obese Class II (BMI 35-40)',
      healthRisk: 'Very High',
    };
  } else {
    return {
      category: 'Obese Class III',
      categoryDescription: 'Obese Class III (BMI ≥ 40)',
      healthRisk: 'Extremely High',
    };
  }
}

/**
 * Helper function to convert BMI category to a color-coded severity
 */
export function getBMICategorySeverity(category: BMIResult['category']): 'success' | 'info' | 'warn' | 'error' {
  switch (category) {
    case 'Normal weight':
      return 'success';
    case 'Underweight':
    case 'Overweight':
      return 'warn';
    case 'Obese Class I':
    case 'Obese Class II':
    case 'Obese Class III':
      return 'error';
    default:
      return 'info';
  }
}
